import React, { useState } from 'react';
import { Box, Typography, List, ListItemButton, Table, TableHead, TableRow, ListItemText, Paper, TextField, TableCell, TableBody, Alert } from '@mui/material';
import '../style/Components.css';
import ButtonBox from './ButtonBox';
import IconButtonBox from './IconButtonBox';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useThingStore from '../stores/ThingStore';
import { ThingShape, ThingBucketType } from '../types/ThingType';
import DialogBox from './DialogBox';
import TextInputBox from './TextInputBox';
import SelectBox from './SelectBox';
import { getRandomId } from '../utils/functions';
import ThingApi from '../api/ThingApi';

interface ThingBucketProps {
    title?: string;
}

const ThingBucket: React.FC<ThingBucketProps> = ({
    title,
}) => {
    const { labelBuckets, setLabelBuckets, thingObj } = useThingStore();
    const [error, setError] = useState('');
    const [isAddShape, setIsAddShape] = useState(false);
    const [delOpen, setDelOpen] = useState(false);

    const [formValues, setFormValues] = useState({
        editFlag: false,
        selectedBucket: '',
        selectedBucketView: '',
        showEditTitle: false,
        editTitle: '',
        showEditShape: false,
        editShapeId: '',
        editViewShapeId: '',
        editAlias: '',
        editValueType: '',
        editUnit: '',
        fromCreate: true,
        isNewBucket: false,
        isNewShape: false,
    });

    const handleOpen = (type: string) => {
        if (type === 'bucket') {
            changeFormValues('showEditTitle', true);
        } else {
            changeFormValues('showEditShape', true);
        }
    };
    const handleClose = (type: string) => {
        changeFormValues('fromCreate', true);

        if (type === 'bucket') {
            changeFormValues('showEditTitle', false);
        } else {
            changeFormValues('showEditShape', false);
        }
    };

    const changeFormValues = (field: string, value: any) => {
        setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
    };

    const handleAddBucket = () => {
        setError('');
        if (formValues.editTitle) {
            if (!labelBuckets.some(bucket => bucket.id === formValues.editTitle)) {
                const newBucket: ThingBucketType = {
                    id: formValues.editTitle,
                    viewId: getRandomId(),
                    dataShapes: []
                };

                labelBuckets.push(newBucket);

                changeFormValues('editTitle', '');
                handleClose('bucket');
            } else {
                setError('Identity already exists.');
            }
        } else {
            setError('Identity is empty.');
        }
    };

    const handleAddShape = () => {
        setError('');
        if (formValues.editShapeId) {
            if (formValues.editValueType) {
                if (formValues.fromCreate) {
                    let isExist = labelBuckets.find(bucket => bucket.viewId === formValues.selectedBucketView)?.dataShapes
                        .find((shape) => shape.id === formValues.editShapeId);

                    if (!isExist) {
                        const newShape: ThingShape = {
                            id: formValues.editShapeId,
                            viewShapeId: getRandomId(),
                            alias: formValues.editAlias,
                            unit: formValues.editUnit,
                            valueType: formValues.editValueType
                        };

                        labelBuckets.find(bucket => bucket.viewId === formValues.selectedBucketView)?.dataShapes.push(newShape);

                        clearShapeData();

                        handleClose('shape');
                    } else {
                        setError('Identity already exists.');
                    }

                } else {
                    let obj = labelBuckets.find(bucket => bucket.viewId === formValues.selectedBucketView)?.dataShapes
                        .find((shape) => shape.id === formValues.editShapeId);

                    if (obj && obj.viewShapeId !== formValues.editViewShapeId) {
                        setError('Identity already exists.');
                    } else {
                        const newShape = labelBuckets.find(
                            bucket => bucket.viewId === formValues.selectedBucketView)?.dataShapes
                            .map((shape) => {
                                return shape.viewShapeId === formValues.editViewShapeId
                                    ? {
                                        id: formValues.editShapeId,
                                        alias: formValues.editAlias,
                                        unit: formValues.editUnit,
                                        valueType: formValues.editValueType,
                                        viewShapeId: formValues.editViewShapeId
                                    } : shape
                            }) as ThingShape[];

                        const updatedBuckets = labelBuckets.map((bucket) =>
                            bucket.viewId === formValues.selectedBucketView && formValues.selectedBucket
                                ? { ...bucket, dataShapes: newShape ? newShape : [] }
                                : bucket
                        );

                        setLabelBuckets(updatedBuckets);
                        clearShapeData();
                        handleClose('shape');
                    }

                }
            } else {
                setError('Value type is required.');
            }
        } else {
            setError('Identity is empty.');
        }

        setIsAddShape(false);
    }

    const modifyTitle = () => {

        if (formValues.editFlag) {
            if (!labelBuckets.some(bucket => bucket.id === formValues.selectedBucket)) {
                const updatedBuckets = labelBuckets.map((bucket) =>
                    bucket.viewId === formValues.selectedBucketView && formValues.selectedBucket
                        ? { ...bucket, id: formValues.selectedBucket }
                        : bucket
                );

                setLabelBuckets(updatedBuckets);
            }
        }

        changeFormValues('editFlag', !formValues.editFlag);

    };

    const openDelete = () => {
        if (formValues.selectedBucketView !== '') {
            setDelOpen(true);
        }
    }

    const deleteBucket = async () => {
        if (formValues.selectedBucketView !== '') {
            if (labelBuckets.some(bucket => bucket.viewId === formValues.selectedBucketView)) {
                if (labelBuckets.some(bucket => bucket.viewId === bucket.id)) {
                    await ThingApi.deleteBucket(thingObj.id || '', formValues.selectedBucketView);
                }

                const updatedBuckets = labelBuckets.filter(bucket => bucket.viewId !== formValues.selectedBucketView);
                setLabelBuckets(updatedBuckets);
            }
            changeFormValues('selectedBucket', '');
            changeFormValues('selectedBucketView', '');
        }

        setDelOpen(false);
    }

    const editShape = (index: number) => {
        let shape = labelBuckets.find(bucket => bucket.viewId === formValues.selectedBucketView)?.dataShapes;

        if (shape) {

            if (shape[index].id === shape[index].viewShapeId) {
                changeFormValues('isNewShape', false);
            } else {
                changeFormValues('isNewShape', true);
            }

            changeFormValues('editShapeId', shape[index].id);
            changeFormValues('editViewShapeId', shape[index].viewShapeId);
            changeFormValues('editAlias', shape[index].alias);
            changeFormValues('editValueType', shape[index].valueType);
            changeFormValues('editUnit', shape[index].unit);
            changeFormValues('fromCreate', false);

            handleOpen('shape');
        }

    }

    const deleteShape = (index: number) => {
        const updatedBuckets = labelBuckets.map((bucket) =>
            bucket.viewId === formValues.selectedBucketView && formValues.selectedBucket
                ? { ...bucket, dataShapes: bucket.dataShapes.filter((_, i) => i !== index) }
                : bucket
        );

        setLabelBuckets(updatedBuckets);
    };


    const updateShapeData = (bucket: any) => {
        changeFormValues('selectedBucket', bucket.id);
        changeFormValues('selectedBucketView', bucket.viewId);

        if (bucket.id !== bucket.viewId) {
            changeFormValues('isNewBucket', true);
        } else {
            changeFormValues('isNewBucket', false);
        }
    }

    const clearShapeData = () => {
        changeFormValues('editShapeId', '');
        changeFormValues('editAlias', '');
        changeFormValues('editValueType', '');
        changeFormValues('editUnit', '');
    }

    return (
        <Box sx={{ mb: 4, display: 'flex', gap: 4 }}>
            <Box className='w220'>
                <Typography variant="h5" gutterBottom>{title}</Typography>
                <ButtonBox
                    text='Add a bucket'
                    onClick={() => handleOpen('bucket')}
                    icon={<AddIcon />} />
                <List sx={{ width: 220, backgroundColor: '#f9f9f9', height: 200, overflowY: 'auto' }}>
                    {labelBuckets.map((bucket, index) => (
                        <ListItemButton
                            selected={bucket.id === formValues.selectedBucket}
                            onClick={() => updateShapeData(bucket)}
                            key={bucket.id}
                        >
                            <ListItemText primary={bucket.id} />
                        </ListItemButton>
                    ))}
                </List>

            </Box>

            {formValues.selectedBucket &&
                <Box className='flex1'>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {formValues.editFlag ?
                                    (
                                        <TextField
                                            value={formValues.selectedBucket}
                                            variant="standard"
                                            size="small"
                                            style={{ width: "100px" }}
                                            onChange={(e) => changeFormValues('selectedBucket', e.target.value)}
                                        />
                                    ) :
                                    (
                                        <Typography variant="h6">{formValues.selectedBucket}</Typography>
                                    )
                                }

                                {formValues.isNewBucket && (
                                    <IconButtonBox
                                        icon={<EditIcon />}
                                        onClick={modifyTitle} />
                                )}
                            </Box>
                            <Box>
                                <IconButtonBox
                                    icon={<DeleteOutlineOutlinedIcon />}
                                    text='Delete the bucket'
                                    onClick={openDelete} />
                                <ButtonBox
                                    text='Add a shape'
                                    onClick={() => { changeFormValues('isNewShape', true); setIsAddShape(true); handleOpen('shape') }}
                                    icon={<AddIcon />} />
                            </Box>
                        </Box>

                        <Table sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Shape ID</TableCell>
                                    <TableCell>Value type</TableCell>
                                    <TableCell>Alias</TableCell>
                                    <TableCell>Unit</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {labelBuckets
                                    .find(bucket => bucket.viewId === formValues.selectedBucketView)?.dataShapes
                                    .map((shape, index) => (
                                        <TableRow key={shape.id}>
                                            <TableCell>{shape.id}</TableCell>
                                            <TableCell>{shape.valueType}</TableCell>
                                            <TableCell>{shape.alias}</TableCell>
                                            <TableCell>{shape.unit}</TableCell>
                                            <TableCell>
                                                <IconButtonBox icon={<EditIcon />} onClick={() => editShape(index)} />
                                                <IconButtonBox icon={<DeleteOutlineOutlinedIcon />} onClick={() => deleteShape(index)} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Box>
            }
            <DialogBox
                open={formValues.showEditTitle}
                title="Add a bucket"
                dialogContent={
                    <>
                        {error && (
                            <Alert
                                severity="error"
                                icon={<ErrorOutlineIcon />}
                                className='login_error_alert'
                            >
                                {error}
                            </Alert>
                        )}
                        <TextInputBox
                            label="Identity"
                            value={formValues.editTitle}
                            onChange={(e) => changeFormValues('editTitle', e.target.value)}
                        />
                    </>
                }
                onCancel={() => handleClose('bucket')}
                onConfirm={handleAddBucket}
                confirmLabel="Add"
            />
            <DialogBox
                open={formValues.showEditShape}
                title="Add a shape"
                dialogContent={
                    <>
                        {error && (
                            <Alert
                                severity="error"
                                icon={<ErrorOutlineIcon />}
                                className='login_error_alert'
                            >
                                {error}
                            </Alert>
                        )}
                        <TextInputBox
                            label="Identity"
                            placeholder="Shape ID"
                            required={true}
                            value={formValues.editShapeId}
                            disabled={!formValues.isNewShape}
                            onChange={(e) => changeFormValues('editShapeId', e.target.value)}
                        />
                        <TextInputBox
                            label="Alias"
                            placeholder="Name"
                            value={formValues.editAlias}
                            onChange={(e) => changeFormValues('editAlias', e.target.value)}
                        />
                        <SelectBox
                            label="Value type"
                            name="valueType"
                            required={true}
                            value={formValues.editValueType}
                            disabled={!formValues.isNewShape && !isAddShape}
                            options={[
                                { value: 'string', label: 'String' },
                                { value: 'integer', label: 'Integer' },
                                { value: 'float', label: 'Float' },
                                { value: 'hex', label: 'Hex' },
                                { value: 'boolean', label: 'Boolean' },
                                { value: 'coordinate', label: 'Coordinate' },
                                { value: 'json', label: 'JSON' },
                                { value: 'array', label: 'Array' },
                            ]}
                            onChange={(e) => changeFormValues('editValueType', e.target.value)}
                        />
                        <TextInputBox
                            label="Unit"
                            placeholder="Unit"
                            value={formValues.editUnit}
                            onChange={(e) => changeFormValues('editUnit', e.target.value)}
                        />
                    </>
                }
                onCancel={() => { setIsAddShape(false); handleClose('shape'); clearShapeData(); }}
                onConfirm={handleAddShape}
                confirmLabel={formValues.fromCreate ? "Add" : "Update"}
            />
            <DialogBox
                open={delOpen}
                title='Delete'
                dialogContent={
                    <>
                        {formValues.selectedBucket} will be permanently deleted.
                    </>
                }
                showCancel={true}
                onCancel={() => setDelOpen(false)}
                onConfirm={deleteBucket}
                isError={true}
                confirmLabel='Delete'
            />
        </Box>
    );
};

export default ThingBucket;
